import {EAnnotLevel, ISkAnnot, ISkTextAnnot, SkAnnotBase} from "lib/edit/schema/schemaAnnots";
import {SchemaDom, SkNode} from "lib/edit/schema/schema";
import {DOM, ENodeType} from "lib/commons/xml/dom";
import {XmlTypedHouse} from "lib/edit/ot/xmlTypedHouse";
import {IXAddr, XA} from "lib/commons/xml/xAddr";
import {SkRuleAttr, SkRuleChoice, SkRuleDoc, SkRuleElt} from "lib/edit/schema/schemaPatterns";
import {SkMatcherAnyAttr, SkMatcherAnyElt} from "lib/edit/schema/schemaMatchers";
import {XmlAddrState, XmlRangeState} from "lib/edit/ot/xmlHouse";
import {MsgOt} from "lib/edit/ot/houseOt";
import {LANG} from "lib/commons/lang";
import IS_element = DOM.IS_element;
import IS_text = DOM.IS_text;


export interface ISkDiffAnnot extends ISkAnnot {
	diffSession: IXmlDiffSession
}

//Déplacé dans WEDLET pour lazy loading du diff.
//export function isSkDiffAnnot(a: ISkAnnot): a is ISkDiffAnnot {return a && ((a as ISkDiffAnnot).diffSession != null)}


export interface IXmlDiffSession {

	mode: EXmlDiffMode

	/** Nombre de différences perceptibles par l'utilisateur. */
	diffFound: number;

	annots: ISkDiffAnnot[]

	computeAnnots(schemaDom: SchemaDom, annotsToAdd: ISkDiffAnnot[]): void
}

export enum EXmlDiffMode {
	diff,
	patch
}

export class XmlDiffSession implements IXmlDiffSession {

	annots: ISkDiffAnnot[];

	diffFound: number = 0;

	//protected notFound: number = 0;

	constructor(public diffDoc: Document, public mode: EXmlDiffMode, public root: IXAddr | null) {}

	computeAnnots(schemaDom: SchemaDom, annotsToAdd: ISkDiffAnnot[]) {
		for (let diff = this.diffDoc.documentElement.firstElementChild; diff; diff = diff.nextElementSibling) {
			this.buildAnnots(diff, schemaDom, annotsToAdd, this.root ? XA.findDomLast(this.root, schemaDom.nodeRoot) : null);
		}
		this.annots = annotsToAdd;
	}

	protected buildAnnots(diffElt: Element, schemaDom: SchemaDom, annotsToAdd: ISkDiffAnnot[], root: Node | null) {
		if (diffElt.namespaceURI !== DIFF_NS) return;
		const node = findDiffNode(diffElt.getAttribute("nodeXp") || diffElt.getAttribute("parentXp"), schemaDom.document);
		if (node) {
			if (root) {
				if (diffElt.localName === "mixCt") {
					if (!DOM.isAncestor(root, node)) {
						//ce mixCt n'est pas dans la descendance de root.
						if (DOM.isAncestor(node, root)) {
							//Par contre notre root est dans ce mixCt, il faut rentrer dans sa définition pour trouver les diff internes.
							const filter = (n: Node): n is Element => n.namespaceURI === DIFF_NS && (n.localName === "val" || n.localName === "add" || n.localName === "rem" || n.localName === "attr" || n.localName === "replace" || n.localName === "mixCt");
							for (let subDiff = DOM.findNext(diffElt, diffElt, filter); subDiff; subDiff = DOM.findNextUncle(subDiff, diffElt, filter)) {
								this.buildAnnots(subDiff, schemaDom, annotsToAdd, null);
							}
							return;
						} else {
							//mixCt ailleurs, on sort
							return;
						}
					}
				} else {
					//Autre diff, elle doit être dans la descendance de root
					if (!DOM.isAncestor(root, node)) return;
				}
			}
			const skNode = schemaDom.getSkNode(node.nodeType === ENodeType.element ? node : node.nodeType === ENodeType.attribute ? (node as Attr).ownerElement : node.parentNode);
			if (!skNode) {
				console.log("SkNode not found for:::", node);
				//this.notFound++;
				return;
			}
			let annot: DiffAnnotBase;
			switch (diffElt.localName) {
			case "add":
				const insAfter = diffElt.getAttribute("insertAfterXp");
				const offset = insAfter ? DOM.computeOffset(findDiffNode(insAfter, node), -1) + 1 : 0;
				annot = new DiffAnnotForeign(this, node).initForeignNode(skNode, offset, diffElt.firstElementChild || diffElt.firstChild);
				this.diffFound++;
				break;
			case "rem":
				annot = new DiffAnnotMark(this, node);
				this.diffFound++;
				break;
			case "val":
				annot = new DiffAnnotValue(this, node).initValue(diffElt.textContent);
				this.diffFound++;
				break;
			case "attr":
				if (IS_element(node)) {
					const currAtt = diffElt.getAttribute("lName");
					const v = diffElt.getAttribute("value");
					const newAttName = diffElt.getAttribute("newQName");
					if (newAttName) {
						//nouvel attribut
						const newAttNs = diffElt.getAttribute("newNs");
						if (currAtt) {
							//Renommage
							const att = node.getAttributeNodeNS(diffElt.getAttribute("ns"), currAtt);
							const a = new DiffAnnotReplace(this, att, node);
							a.foreign.initForeignAtt(skNode, newAttNs, newAttName, v);
							annot = a;
						} else {
							//Nouvel attribut
							annot = new DiffAnnotForeign(this, node).initForeignAtt(skNode, newAttNs, newAttName, v);
						}
					} else {
						const att = node.getAttributeNodeNS(diffElt.getAttribute("ns"), currAtt);
						if (v != null) {
							//changement de valeur
							annot = new DiffAnnotValue(this, att).initValue(v);
						} else {
							//rem attr
							annot = new DiffAnnotMark(this, att);
						}
					}
					this.diffFound++;
				}// else this.notFound++;
				break;
			case "replace":
				const p = node instanceof Attr ? node.ownerElement : node.parentNode;
				const an = new DiffAnnotReplace(this, node, p);
				const foreign = diffElt.firstElementChild || diffElt.firstChild;
				if (IS_element(foreign) && foreign.namespaceURI === DIFF_NS && foreign.localName === "attrHolder") {
					for (let i = foreign.attributes.length - 1; i >= 0; i--) {
						const att = foreign.attributes.item(i);
						if (att.localName != "xmlns" && att.prefix !== "xmlns") {
							an.foreign.initForeignAtt(schemaDom.getSkNode(p), att.namespaceURI, att.nodeName, att.value);
							break;
						}
					}
				} else {
					an.foreign.initForeignNode(schemaDom.getSkNode(p), DOM.computeOffset(node), foreign);
				}
				annot = an;
				this.diffFound++;
				break;
			case "mixCt":
				const mixAnnot = annot = new DiffAnnotMixCt(this, node).initRoot(skNode, diffElt);
				const tw = diffElt.ownerDocument.createTreeWalker(diffElt);
				while (tw.nextNode()) {
					const n = tw.currentNode;
					if (IS_element(n) && n.namespaceURI === DIFF_NS) {
						switch (n.localName) {
						case "mcDel": {
							//find the deleted node
							const delNode = findDiffNode(n.getAttribute("nodeXp"), node);
							if (!delNode) break; //raceCond
							const subAnnot = IS_element(delNode) ?
								new DiffAnnotInParaMark(this, delNode)
								: new DiffAnnotStrMark(this, delNode).initRange(parseInt(n.getAttribute("start")), parseInt(n.getAttribute("end")));
							addAnnot(subAnnot, schemaDom, annotsToAdd);
							this.diffFound++;
							break;
						}
						case "mcIns": {
							//On a besoin d'instancier maintenant ces annots pour pouvoir les ajuster en live.
							//On a deux annots : une dans le document courant, une dans le para étranger.
							const annotCurrent = new DiffAnnotInject(this, findDiffNode(n.getAttribute("nodeXp"), node));
							annotCurrent.initGap(parseInt(n.getAttribute("offset")));
							const contentElt = DOM.findFirstChild(n, IS_element) != null; //mcIns avec text ou avec elt.
							//annot dans le foreign para, node sera affecté à l'init de ce foreign para.
							const annotForeign = contentElt ? new DiffAnnotMarkInForeignPara(mixAnnot.paraSession, null) : new DiffAnnotStrMarkInForeignPara(mixAnnot.paraSession, null);
							(n as IDiffMixAnnotPointer).annotInMixCt = annotForeign; //pour retrouver l'annot et fixer l'ancrage vis à vis du foreign para.
							annotCurrent.initForeignAnnot(annotForeign);
							addAnnot(annotCurrent, schemaDom, annotsToAdd);
							this.diffFound++;
							break;
						}
						case "mcUnwrapStart": {
							const subAnnot = new DiffAnnotWrapMark(this, findDiffNode(n.getAttribute("nodeXp"), node));
							subAnnot.wrap = "full";
							addAnnot(subAnnot, schemaDom, annotsToAdd);
							this.diffFound++;
							break;
						}
						case "mcUnwrapEnd":
							//traité dans mcUnwrapStart
							break;
						case "mcWrap": {
							const annotCurrent = new DiffAnnotInject(this, findDiffNode(n.getAttribute("nodeXp"), node));
							annotCurrent.initGap(parseInt(n.getAttribute("offset")));
							//annot dans le foreign para, node sera affecté à l'init de ce foreign para.
							const annotForeign = new DiffAnnotWrapMarkInForeignPara(mixAnnot.paraSession, null);
							annotForeign.wrap = "full";
							(n as IDiffMixAnnotPointer).annotInMixCt = annotForeign; //pour retrouver l'annot et fixer l'ancrage vis à vis du foreign para.
							annotCurrent.initForeignAnnot(annotForeign);
							addAnnot(annotCurrent, schemaDom, annotsToAdd);
							this.diffFound++;
							break;
						}
						case "mcMoveEnd": {
							const subAnnot = new DiffAnnotWrapMark(this, findDiffNode(n.getAttribute("nodeXp"), node));
							subAnnot.wrap = "end";
							addAnnot(subAnnot, schemaDom, annotsToAdd);
							this.diffFound++;
							break;
						}
						case "add":
						case "rem":
						case "val":
						case "attr":
						case "replace":
							const xp = n.getAttribute("nodeXp") || n.getAttribute("parentXp");
							if (xp) {
								//une addresse spécifiée
								if (xp.charAt(0) === '/') {
									//nodeXp non relatif au mixCt: on est hors du mixedContent, ie modifs au sein même des metas.
									this.buildAnnots(n, schemaDom, annotsToAdd, null);
								} else {
									switch (n.localName) {
									case "rem": {
										//Cas : balise meta dans le doc courant, absent du doc candidat.
										const subAnnot = new DiffAnnotInParaMark(this, findDiffNode(xp, node));
										addAnnot(subAnnot, schemaDom, annotsToAdd);
										if (!(n.previousElementSibling?.localName === "mcUnwrapStart")) this.diffFound++; // diffFound++ si ce n'est pas une meta d'une balise wrap ajoutée dans le doc courant.
										break;
									}
									case "add": {
										//Cas : balise meta dans le doc candidat, absent du doc courant.
										//console.log(":::diff MetaTag in foreign doc:::", n);
										const annotCurrent = new DiffAnnotInject(this, findDiffNode(xp, node));
										annotCurrent.initGap(parseInt(n.getAttribute("offset")));
										//annot dans le foreign para, node sera affecté à l'init de ce foreign para.
										const annotForeign = new DiffAnnotMarkInForeignPara(mixAnnot.paraSession, null);
										(n as IDiffMixAnnotPointer).annotInMixCt = annotForeign; //pour retrouver l'annot et fixer l'ancrage vis à vis du foreign para.
										annotCurrent.initForeignAnnot(annotForeign);
										addAnnot(annotCurrent, schemaDom, annotsToAdd);
										//this.diffFound++; ajouté via mcWrap
										break;
									}
									}
								}
							}
							break;
						}
					}
				}
				break;
			}
			if (annot) {
				annotsToAdd.push(annot);
				(skNode.extAnnots || (skNode.extAnnots = [])).push(annot);
			} // else this.notFound++;
		} //else this.notFound++;
	}

}

function addAnnot(annot: ISkDiffAnnot, schemaDom: SchemaDom, annotsToAdd: ISkDiffAnnot[]) {
	annotsToAdd.push(annot);
	const n = annot.anchorNode;
	const skNode = schemaDom.getSkNode(IS_text(n) ? n.parentNode : n);
	(skNode.extAnnots || (skNode.extAnnots = [])).push(annot);
}

/** Diff session pour l'affichage de chaque paragraphe étranger. */
class ParaDiffSession implements IXmlDiffSession {

	mode: EXmlDiffMode;

	annots: ISkDiffAnnot[];

	/** pas utilisé. */
	get diffFound(): number {return 0}

	constructor(paraAnnot: IDiffAnnotMixCt) {
		//On inverse le mode d'affichage
		this.mode = paraAnnot.diffSession.mode === EXmlDiffMode.patch ? EXmlDiffMode.diff : EXmlDiffMode.patch;
		this.annots = [];
	}

	computeAnnots(schemaDom: SchemaDom, annotsToAdd: ISkDiffAnnot[]): void {
		//console.log(":::ParaDiffSession.annots::",this.annots);
		for (let a of this.annots) {
			const skNode = schemaDom.getSkNode(a.anchorNode.parentNode);
			if (skNode) {
				(skNode.extAnnots || (skNode.extAnnots = [])).push(a);
				annotsToAdd.push(a);
			}
		}
	}
}

export function findDiffNode(nodeXp: string, from: Node): Node {
	const absolute = nodeXp[0] === "/";
	let node: Node = absolute ? from.ownerDocument || from : from;
	let xp = absolute ? nodeXp.substring(1) : nodeXp;
	nextPart:while (xp) {
		if (xp.startsWith("@")) {
			const r = /@(?:{(.*)})?(.*)/.exec(xp);
			return r[1] ? (node as Element).getAttributeNodeNS(r[1], r[2]) : (node as Element).getAttributeNode(r[2]);
		}
		let nextSlash = xp.indexOf('/');
		let p: string;
		if (nextSlash > 0) {
			p = xp.substring(0, nextSlash);
			xp = xp.substring(nextSlash + 1);
		} else if (nextSlash === 0) {
			xp = xp.substring(1);
			continue nextPart;
		} else {
			p = xp;
			xp = null;
		}
		let typeNode: ENodeType;
		let name: string;
		let idxCount: number;
		if (p.startsWith("*:")) {
			typeNode = ENodeType.element;
			idxCount = p.indexOf("[", 3);
			if (idxCount > 0) {
				name = p.substring(2, idxCount);
			} else {
				idxCount = undefined;
				name = p.substring(2);
			}
		} else if (p.startsWith("#text")) {
			typeNode = ENodeType.text;
			idxCount = 5;
		} else if (p.startsWith("#comment")) {
			typeNode = ENodeType.comment;
			idxCount = 8;
		} else if (p.startsWith("#pi")) {
			typeNode = ENodeType.pi;
			idxCount = 3;
		}
		let count = idxCount < p.length ? parseInt(p.substring(idxCount + 1, p.length - 1)) : 1;
		for (let ch = node.firstChild; ch; ch = ch.nextSibling) {
			if (ch.nodeType === typeNode && (name === undefined || name === ch.localName)) {
				if (--count === 0) {
					node = ch;
					continue nextPart;
				}
			}
		}
		//pas trouvé!
		console.log("Node not found:::", nodeXp);
		return null;
	}
	return node;
}


export function applyDiffs(on: Node, diffs: ISkDiffAnnot[], onXpDepth: number) {
	//console.log("applyDiffs before:::", on.cloneNode(true), diffs);
	for (let i = diffs.length - 1; i >= 0; i--) {
		//On traite les annots dans l'ordre inverse pour préserver les offsets.
		const diff = diffs[i];
		if (diff.type === "diffValue") {
			const target = XA.findDomLast(diff.start.slice(onXpDepth), on);
			if (target) target.nodeValue = (diff as IDiffAnnotValue).otherValue;
			else console.log("applyDiff start not found", diff);
		} else if (diff.type === "diffMark") {
			const target = XA.findDomLast(diff.start.slice(onXpDepth), on);
			if (target) if (target instanceof Attr) target.ownerElement.removeAttribute(target.nodeName); else target.parentElement.removeChild(target);
			else console.log("applyDiff start not found", diff);
		} else if (diff.type === "diffForeign") {
			const d = diff as IDiffAnnotForeign;
			const ctn = XA.findDomContainer(diff.start.slice(onXpDepth), on);
			const offset = XA.last(diff.start) as number;
			if (ctn) {
				if (ctn instanceof CharacterData) {
					if (d.strValue) ctn.insertData(offset, d.strValue);
					else {
						ctn.parentNode.insertBefore(on.ownerDocument.importNode(d.foreignNode, true), DOM.splitDomCharacterData(ctn, offset));
					}
				} else if (d.attName) {
					if (ctn instanceof Element) ctn.setAttributeNS(d.attNs, d.attName, d.strValue);
					else console.log("applyDiff invalid: attr can only be set on element", diff, ctn);
				} else {
					ctn.insertBefore(on.ownerDocument.importNode(d.foreignNode, true), ctn.childNodes.item(offset));
				}
			} else console.log("applyDiff start not found", diff);
		} else if (diff.type === "diffMix") {
			const target = XA.findDomLast(diff.start.slice(onXpDepth), on);
			if (target) {
				target.textContent = null;//clear le contenu construit par mixCtToForeign()
				mixCtToForeign((diff as IDiffAnnotMixCt).diffRoot, target);
			} else console.log("applyDiff start not found", diff);
		} else if (diff.type === "diffStrMark") {
			const txt = XA.findDomLast(diff.start.slice(onXpDepth), on);
			if (txt instanceof CharacterData || txt instanceof Attr) {
				txt.nodeValue = LANG.stringDelete(txt.nodeValue, (diff as IDiffAnnotStrMark).offsetStart, diff.len);
			} else console.log("applyDiff start not a Text or Attr", diff, txt);
		} else if (diff.type === "diffReplace") {
			const target = XA.findDomLast(diff.start.slice(onXpDepth), on);
			if (target) {
				target.parentElement.insertBefore(on.ownerDocument.importNode((diff as IDiffAnnotReplace).foreign.foreignNode, true), target);
				target.parentElement.removeChild(target);
			} else console.log("applyDiff start not found", diff);
		} else if (!diff.type) {
			//No op. Certains Diff sans type sont des marques techniques comme DiffAnnotInject
		} else {
			console.log("Unknown diff for apply on Node:", diff);
		}
	}
	//console.log("applyDiffs after:::", on.cloneNode(true));
}


export function mixCtToForeign(from: Node, target: Node) {
	if (from.namespaceURI === DIFF_NS) {
		switch (from.localName) {
		case "mcIns": {
			//console.log("mixCtToForeign::mcIns:",(from as IDiffMixAnnotPointer).annotInMixCt);
			//On ancre l'annot
			const contentElt = (from as Element).firstElementChild;
			if (contentElt) {
				//mcIns d'un élément du para (objectLeaf...).
				const a = (from as IDiffMixAnnotPointer).annotInMixCt as DiffAnnotMarkInForeignPara;
				a.anchorNode = target.appendChild(target.ownerDocument.importNode(contentElt, true));
			} else {
				//mcIns d'un texte.
				const a = (from as IDiffMixAnnotPointer).annotInMixCt as DiffAnnotStrMarkInForeignPara;
				const text = from.textContent;
				const txtNode = target.lastChild && IS_text(target.lastChild) ? target.lastChild : target.appendChild(target.ownerDocument.createTextNode(""));
				const start = txtNode.nodeValue.length;
				txtNode.appendData(text);
				a.anchorNode = txtNode;
				a.initRange(start, start + text.length);
			}
			break;
		}
		case "mcWrap": {
			mixCtToForeign((from as Element).firstElementChild, target);
			//for (let ch = (from as Element).firstElementChild.firstChild; ch; ch = ch.nextSibling) mixCtToForeign(ch, target);
			const a = (from as IDiffMixAnnotPointer).annotInMixCt as DiffAnnotWrapMarkInForeignPara;
			if (a) a.anchorNode = target.lastChild;
			break;
		}
		case "add": {
			target.appendChild(target.ownerDocument.importNode((from as Element).firstElementChild, true));
			const a = (from as IDiffMixAnnotPointer).annotInMixCt as DiffAnnotMarkInForeignPara;
			if (a) a.anchorNode = target.lastChild;
			break;
		}
		case "mixCt":
			for (let ch = from.firstChild; ch; ch = ch.nextSibling) mixCtToForeign(ch, target);
			break;
			//case "mcDel", "mcUnwrapStart", "mcUnwrapEnd":
			//	break;
		}
	} else if (IS_text(from)) {
		if (target.lastChild && IS_text(target.lastChild)) {
			target.lastChild.appendData(from.nodeValue);
		} else {
			target.appendChild(target.ownerDocument.createTextNode(from.nodeValue));
		}
	} else {
		const sub = target.appendChild(target.ownerDocument.importNode(from, false));
		for (let ch = from.firstChild; ch; ch = ch.nextSibling) mixCtToForeign(ch, sub);
	}
}

/**
 *
 */
abstract class DiffAnnotBase extends SkAnnotBase implements ISkDiffAnnot {

	abstract type: string;

	get level() {return EAnnotLevel.diff}

	constructor(public diffSession: IXmlDiffSession, node: Node) {
		super();
		this.anchorNode = node;
	}

	equals(other: ISkAnnot): boolean {return this === other}

	/** non sérialisable. */
	toJSON(): any {}
}

/** Base pour les annots ancrées dans un interstice. Besoin d'être mis à jour par les msg. */
abstract class DiffAnnotInGap extends DiffAnnotBase {
	protected gap?: XmlAddrState;

	get offset(): number {return this.gap ? XA.last(this.gap.xAddr) as number : undefined}

	initGap(offset: number) {
		this.gap = new XmlAddrState().init(null, null, XA.append(XA.fromNode(this.anchorNode), offset));
	}

	adjustBy(msg: MsgOt): void {
		if (this.gap) this.gap.update(msg);
	}
}

/**
 * Marquage de noeuds ou attributs complets (elements, comments... ou attributs)
 * - Mode diff : marque un noeud du document courant comme ajouté,
 * - Mode patch : marque un noeud du document courant comme supprimé.
 */
export interface IDiffAnnotMark extends ISkDiffAnnot {
	type: "diffMark"

	/** Pour les para :
	 * "full" : balise inline ajoutée dans le doc courant
	 * "end" : fin de cette balise inline déplacée.
	 */
	wrap?: "full" | "end"
}

class DiffAnnotMark extends DiffAnnotBase implements IDiffAnnotMark {
	static TYPE: "diffMark" = "diffMark";

	get type() {return DiffAnnotMark.TYPE}

	get level() {return this.diffSession.mode === EXmlDiffMode.diff ? EAnnotLevel.diffadd : EAnnotLevel.diffrem}

	getLabel(): string {return this.diffSession.mode === EXmlDiffMode.diff ? "Contenu ajouté" : "Contenu supprimé"}
}

class DiffAnnotInParaMark extends DiffAnnotMark {
	get level() {return this.diffSession.mode === EXmlDiffMode.diff ? EAnnotLevel.diffaddHidden : EAnnotLevel.diffremHidden}
}

class DiffAnnotWrapMark extends DiffAnnotMark {

	wrap?: "full" | "end";

	get level() {return this.wrap === "end" ? EAnnotLevel.diffHidden : this.diffSession.mode === EXmlDiffMode.diff ? EAnnotLevel.diffaddHidden : EAnnotLevel.diffremHidden}

	getLabel(): string {
		if (this.wrap === "end") return "Fin de la balise encadrante déplacée";
		return this.diffSession.mode === EXmlDiffMode.diff ? "Balise encadrante ajoutée" : "Balise encadrante supprimée";
	}
}

/**
 * Annot qui contient un contenu étranger à insérer (elements, comments... ou attributs)
 * - Mode diff = injecte un contenu supprimé dans le doc courant,
 * - Mode patch = injecte un contenu insérable dans le doc courant.
 */
export interface IDiffAnnotForeign extends ISkDiffAnnot {

	type: "diffForeign"

	/** Offset d'insertion. */
	offset?: number

	/** Elément inséré. */
	foreignNode?: Node;

	/** Si attribut. */
	attName?: string
	attNs?: string

	/** Si attribut ou noeud CharacterData text, comment...*/
	strValue?: string

	/** Construit une house avec foreignNode ou l'attribut wrappé ou un document customisé en paramètre. */
	createForeignHouse(customDoc?: Document | DocumentFragment): XmlTypedHouse
}

export class DiffAnnotForeign extends DiffAnnotInGap implements IDiffAnnotForeign {
	static TYPE: "diffForeign" = "diffForeign";

	get type() {return DiffAnnotForeign.TYPE}

	foreignNode?: Node;

	attName?: string;
	attNs?: string;
	strValue?: string;

	skNodeParent: SkNode;

	get len(): number {return 0}

	get anchor() {return XA.from(this.anchorNode)}

	get start() {return XA.append(XA.from(this.anchorNode), this.attName || this.offset)}

	get level() {return this.diffSession.mode === EXmlDiffMode.diff ? EAnnotLevel.diffrem : EAnnotLevel.diffadd}

	constructor(diffSession: IXmlDiffSession, anchor: Node) {
		super(diffSession, anchor);
	}

	getLabel(): string {return this.diffSession.mode === EXmlDiffMode.diff ? "Contenu supprimé" : "Contenu ajouté"}

	initForeignNode(skNodeParent: SkNode, offset: number, foreign: Node): this {
		this.skNodeParent = skNodeParent;
		this.initGap(offset);
		this.foreignNode = foreign;
		if (foreign instanceof CharacterData) this.strValue = foreign.nodeValue;
		return this;
	}

	initForeignAtt(skNodeParent: SkNode, attNs: string, attName: string, attValue: string): this {
		this.skNodeParent = skNodeParent;
		//TODO normalisation préfixe attName avec skNodeParent.schemaDom.schema ?
		this.attName = attName;
		this.attNs = attNs;
		this.strValue = attValue;
		return this;
	}

	createForeignHouse(customDoc: Document | DocumentFragment): XmlTypedHouse {
		if (customDoc || this.foreignNode) {
			let initDoc: Document | DocumentFragment = customDoc;
			if (!initDoc) {
				const doc = initDoc = DOM.newDomDoc();
				if (initDoc.nodeType !== ENodeType.element) {
					//simple noeud texte ou comment supprimé
					initDoc = doc.createDocumentFragment();
				}
				initDoc.appendChild(doc.importNode(this.foreignNode, true));
			}
			const rootRule = new SkRuleDoc().init(new SkRuleChoice().initCard("*").initSubRules(this.skNodeParent.rule.contentRule.findRules(() => true)));
			return this.initHouse(initDoc, this.skNodeParent, rootRule);
		}
		//attribut
		const doc = DOM.newDomDoc();
		const root = doc.appendChild(doc.createElementNS(DIFF_NS, "sd:attrHolder"));
		root.setAttributeNS(this.attNs, this.attName, this.strValue);
		return this.initHouse(doc, this.skNodeParent, DiffAnnotForeign.rootRuleForAtt);
	}

	protected initHouse(initDoc: Document | DocumentFragment, skNodeParent: SkNode, rootRule: SkRuleDoc): XmlTypedHouse {
		return new XmlTypedHouse({
			initialDoc: initDoc,
			schema: skNodeParent.schemaDom.schema,
			schemaDomConfig: {rootRule},
			buildOptions: {autoMutate: true, autoComplete: true, autoNormXml: true, autoNormChars: true, genAnnots: false}
		});
	}

	static rootRuleForAtt = new SkRuleDoc().init(new SkRuleElt().init(SkMatcherAnyElt.SINGLETON, "1", new SkRuleAttr().init(SkMatcherAnyAttr.SINGLETON, false)));
}


/**
 * Annot qui gère un changement de valeur d'un attribut ou d'un noeud CharacterData (text, commpent pi)
 */
export interface IDiffAnnotValue extends ISkDiffAnnot {
	type: "diffValue"
	otherValue: string;
}

class DiffAnnotValue extends DiffAnnotBase implements IDiffAnnotValue {
	static TYPE: "diffValue" = "diffValue";

	otherValue: string;

	get type() {return DiffAnnotValue.TYPE}

	initValue(value: string): this {
		this.otherValue = value;
		return this;
	}

	getLabel(): string {return "Valeur modifiée"}

}

/**
 * Annot qui gère un remplacement d'un contenu dans le doc courant par un contenu étranger.
 * - Mode diff = le contenu étranger est marqué supprimé, le contenu dans le doc courant est ajouté
 * - Mode patch = le contenu étranger est marqué ajouté, le contenu dans le doc courant est supprimé
 */
export interface IDiffAnnotReplace extends ISkDiffAnnot {

	type: "diffReplace"

	foreign: IDiffAnnotForeign

	inCurrent: IDiffAnnotMark
}

export class DiffAnnotReplace extends DiffAnnotBase implements IDiffAnnotReplace {
	static TYPE: "diffReplace" = "diffReplace";

	get type() {return DiffAnnotReplace.TYPE}

	foreign: DiffAnnotForeign;

	inCurrent: DiffAnnotMark;

	constructor(diffSession: IXmlDiffSession, anchor: Node, anchorForForeign: Node) {
		super(diffSession, anchor);
		this.foreign = new DiffAnnotForeign(diffSession, anchorForForeign);
		this.inCurrent = new DiffAnnotMark(diffSession, anchor);
	}

	getLabel(): string {return "Contenu remplacé"}
}


/**
 * Annot englobant les modifications d'un paragraphe.
 */
export interface IDiffAnnotMixCt extends ISkDiffAnnot {
	type: "diffMix"
	/** élément sd:mixCt du schéma Diff. */
	diffRoot: Element
	skNodePara: SkNode
	paraSession: ParaDiffSession
}

class DiffAnnotMixCt extends DiffAnnotBase implements IDiffAnnotMixCt {
	static TYPE: "diffMix" = "diffMix";

	diffRoot: Element;

	skNodePara: SkNode;

	paraSession: ParaDiffSession;

	get type() {return DiffAnnotMixCt.TYPE}

	initRoot(skNodePara: SkNode, root: Element): this {
		this.skNodePara = skNodePara;
		this.diffRoot = root;
		this.paraSession = new ParaDiffSession(this);
		return this;
	}

	getLabel(): string {return "Paragraphe modifié"}
}


/**
 * Marquage d'un fragment d'une string
 * - Mode diff : marque un fragment comme ajouté,
 * - Mode patch : marque un fragment comme supprimé.
 */
export interface IDiffAnnotStrMark extends ISkDiffAnnot, ISkTextAnnot {
	type: "diffStrMark"
}

class DiffAnnotStrMark extends DiffAnnotBase implements IDiffAnnotStrMark {
	static TYPE: "diffStrMark" = "diffStrMark";

	protected range?: XmlRangeState;

	/** offset dans le text/attr */
	get offsetStart(): number {return XA.last(this.range.start) as number}

	get len() {return (XA.last(this.range.end) as number) - (XA.last(this.range.start) as number)}

	get type() {return DiffAnnotStrMark.TYPE}

	get level() {return this.diffSession.mode === EXmlDiffMode.diff ? EAnnotLevel.diffaddHidden : EAnnotLevel.diffremHidden}

	getLabel(): string {return this.diffSession.mode === EXmlDiffMode.diff ? "Texte ajouté" : "Texte supprimé"}

	initRange(startRg: number, endRg: number): this {
		const start = XA.freeze(XA.append(XA.fromNode(this.anchorNode), startRg));
		this.range = new XmlRangeState().init(null, null, start, XA.setAtDepth(start, -1, endRg));
		return this;
	}

	adjustBy(msg: MsgOt): void {
		this.range.update(msg);
		//console.log("DiffAnnotStrMark.adjustBy:::",this.range);
	}
}

export interface IDiffMixAnnotPointer {
	annotInMixCt?: ISkDiffAnnot
}


/**
 * Marque des mcIns dans le para du document courant.
 * Pas de représentation, mais nécessaire pour Ajustement des edits concurrents.
 */
export class DiffAnnotInject extends DiffAnnotInGap {
	static TYPE: "diffInject" = "diffInject";

	get type() {return DiffAnnotInject.TYPE}

	/** Annot dans le document foreign. */
	annotInForeign: DiffAnnotStrMarkInForeignPara | DiffAnnotMarkInForeignPara | DiffAnnotWrapMarkInForeignPara;

	get level() {return EAnnotLevel.diffHidden}

	getLabel(): string {return ""}

	initForeignAnnot(annotInForeign: DiffAnnotStrMarkInForeignPara | DiffAnnotMarkInForeignPara | DiffAnnotWrapMarkInForeignPara) {
		this.annotInForeign = annotInForeign;
		annotInForeign.annotInCurrent = this;
		annotInForeign.diffSession.annots.push(annotInForeign); //pour ajuster avec les edits concurents dans le doc courant.
	}
}

/**
 * Marque des mcIns de type texte dans le para étranger.
 */
export class DiffAnnotStrMarkInForeignPara extends DiffAnnotStrMark {

	diffSession: ParaDiffSession;

	/** Annot dans le document courant. */
	annotInCurrent: DiffAnnotInject;
}

/**
 * Marque des add de type elt dans le para étranger.
 */
export class DiffAnnotMarkInForeignPara extends DiffAnnotMark {

	diffSession: ParaDiffSession;

	/** Annot dans le document courant. */
	annotInCurrent: DiffAnnotInject;
}

/**
 * Marque des wrap de type elt dans le para étranger.
 */
export class DiffAnnotWrapMarkInForeignPara extends DiffAnnotWrapMark {

	diffSession: ParaDiffSession;

	/** Annot dans le document courant. */
	annotInCurrent: DiffAnnotInject;
}


export const DIFF_NS = "scenari.eu:diff:1.0";

